import { observable, action, computed, makeAutoObservable } from 'mobx';
import { debounce } from 'lodash';
import type { DebouncedFunc } from 'lodash';
import { DateTime } from 'src_common/utils/time/time';
// eslint-disable-next-line no-restricted-imports
import { apiCommon } from 'src/api/ApiCommon';
import {
    FreeSpinTransactionsDataItemType,
    FreeSpinParamsType,
    FreeSpinTransactionsResponseDataType,
} from 'src/api/config/accounts/getFreeSpinTransactions';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';

export interface RangeType {
    startDate: Date;
    endDate: Date;
}

interface FilterType {
    status: string;
    type: string;
}

const initialStartDate = DateTime.current().format('YYYY-MM-DD');
const initialEndDate = DateTime.current().format('YYYY-MM-DD');

class PageState {
    @observable public page: number = 1;

    @action public resetPages = (): void => {
        this.page = 1;
    };

    public constructor() {
        makeAutoObservable(this);
    }
}

type TabType = 'all';

type FreeSpinTransactionsResponseType = FreeSpinTransactionsResponseDataType;
type FreeSpinTransactionsItemType = FreeSpinTransactionsDataItemType;
export class FreeSpinTransactionHistoryTabState {
    public readonly perPage: number = 20;
    public readonly pagePerTab: MobxMapAutoNew<TabType, PageState> = new MobxMapAutoNew(() => new PageState());
    private readonly updateFinalRangeDebounced: DebouncedFunc<(range: RangeType) => void>;

    private assetFlowsCurrentCursor: string | null = null;
    private allLoadedPages: Map<number, FreeSpinTransactionsResponseType> = new Map();

    @observable public range: RangeType = {
        startDate: new Date(initialStartDate),
        endDate: new Date(initialEndDate),
    };

    @observable public finalRange: RangeType = {
        startDate: new Date(initialStartDate),
        endDate: new Date(initialEndDate),
    };

    @observable public filterType: FilterType = {
        status: 'all',
        type: 'all',
    };

    @observable public transactionTypeActiveFilter: string | undefined = 'all';
    @observable public transactionStatusActiveFilter: string | undefined = 'all';

    @observable.ref public userAssetFlows: AutoMap<
        [string, string, string | null, number],
        Resource<FreeSpinTransactionsResponseType>
    >;

    public constructor(
        private readonly userId: number,
        public readonly routerBackoffice: RouterBackoffice
    ) {
        this.updateFinalRangeDebounced = debounce(this.setFinalRange, 300);

        this.userAssetFlows = new AutoMap(([startDate, endDate, cursor = null], page = this.page) => {
            return new Resource(async (): Promise<FreeSpinTransactionsResponseType> => {
                const alreadyHasLoadedResponse = this.allLoadedPages.get(page);
                if (alreadyHasLoadedResponse !== undefined) {
                    return await new Promise((resolve) => setTimeout(() => resolve(alreadyHasLoadedResponse), 1000));
                }

                const body = {
                    userId: this.userId,
                    cursor,
                    perPage: this.perPage,
                    startDate: DateTime.from(startDate)?.format('YYYY-MM-DD') ?? initialStartDate,
                    endDate: DateTime.from(endDate)?.format('YYYY-MM-DD') ?? initialEndDate,
                } satisfies FreeSpinParamsType;

                const response = await apiCommon.getFreeSpinTransactions.run(body);

                if (response.transactions.length > 0) {
                    this.savePageResponse(page, response);
                }

                return response;
            }, 30_000);
        });
        makeAutoObservable(this);
    }

    @action private savePageResponse(page: number, response: FreeSpinTransactionsResponseType): void {
        this.allLoadedPages.set(page, { ...response });
    }

    @action private setCurrentCursor(cursor: string | null): void {
        this.assetFlowsCurrentCursor = cursor;
    }

    @computed private get currentCursor(): string | null {
        return this.assetFlowsCurrentCursor;
    }

    @action public handleOnUpdateDate = (startDate: Date, endDate: Date): void => {
        this.allLoadedPages.clear();
        this.setCurrentCursor(null);
        this.range = { startDate, endDate };
        this.updateFinalRangeDebounced({ startDate, endDate });
    };

    @action private setFinalRange = (range: RangeType): void => {
        this.finalRange = range;
        this.pageState.resetPages();
    };

    @computed public get pageState(): PageState {
        return this.pagePerTab.get('all');
    }

    @computed public get page(): number {
        return this.pageState.page;
    }

    @computed public get assetFlowsResponse(): Result<FreeSpinTransactionsResponseType> | null {
        return this.userAssetFlows
            .get([
                this.finalRange.startDate.toString(),
                this.finalRange.endDate.toString(),
                this.currentCursor,
                this.page,
            ])
            .get();
    }

    @computed public get assetFlowsData(): FreeSpinTransactionsResponseType | null {
        if (this.assetFlowsResponse !== null) {
            if (this.assetFlowsResponse.type === 'ready') {
                return this.assetFlowsResponse.value;
            }
        }
        return null;
    }

    @computed public get assetFlowsCurrentCount(): number {
        return 0;
    }

    @computed public get assetFlowsList(): Array<FreeSpinTransactionsItemType> {
        if (this.assetFlowsData !== null) {
            return this.assetFlowsData.transactions;
        }
        return [];
    }

    @computed public get isAssetFlowsListLoading(): boolean {
        return this.assetFlowsResponse?.type === 'loading';
    }

    @computed public get isAssetFlowsListTimeout(): boolean {
        return this.assetFlowsResponse?.type === 'error' && this.assetFlowsResponse.message.includes('Timeout');
    }

    @computed public get shouldDisplayTimeoutMessage(): boolean {
        return !this.isAssetFlowsListLoading && this.isAssetFlowsListTimeout;
    }

    @computed public get shouldDisplayEmptyMessage(): boolean {
        return !this.isAssetFlowsListLoading && this.assetFlowsList.length === 0 && !this.isAssetFlowsListTimeout;
    }

    @computed public get assetFlowsListLength(): number {
        return this.assetFlowsList.length;
    }

    @action public onPageChange = (page: number): void => {
        const cursor = this.assetFlowsData?.cursor;
        this.setCurrentCursor(cursor ?? null);

        this.pageState.page = page;
    };
}
